import { getAuthToken } from './auth/tokenStorage';
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';
import { onboardingLogger } from '../utils/logger';

export interface OnboardingProgress {
  id: string;
  userId: string;
  onboardingMode: 'quick_start' | 'comprehensive';
  currentPhase: string;
  currentQuestionIndex: number;
  questionsAnsweredCount: number;
  conversationContext: any;
  aiConversationId: string | null;
  isPaused: boolean;
  pausedAt: Date | null;
  isCompleted: boolean;
  isSkipped: boolean;
  completedAt: Date | null;
  lastInteractionAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface TaskPriority {
  id: string;
  userId: string;
  taskKey: string;
  priorityScore: number;
  priorityTier: 'critical' | 'high' | 'medium' | 'low';
  reasoning: string;
  influencedBy: string[];
  isCompleted: boolean;
  isSkipped: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface StartOnboardingResponse {
  success: boolean;
  data: OnboardingProgress;
}

export interface PrioritiesResponse {
  success: boolean;
  data: TaskPriority[];
}

class OnboardingService {
  private async getAuthHeader() {
    const token = await getAuthToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  /**
   * Start onboarding with specified mode
   */
  async startOnboarding(
    mode: 'quick_start' | 'comprehensive'
  ): Promise<OnboardingProgress> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.post<StartOnboardingResponse>(
        `${API_URL}/api/onboarding/start`,
        { mode },
        config
      );
      return response.data.data;
    } catch (error: any) {
      onboardingLogger.error('Error starting onboarding:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to start onboarding'
      );
    }
  }

  /**
   * Get current onboarding progress.
   *
   * Also exposes the new-wizard state (onboardingVersion + onboardingStep)
   * on the same endpoint via getProgressAndWizardState() below so callers
   * that need both (like SplashScreen) don't have to make two round trips.
   */
  async getProgress(): Promise<OnboardingProgress | null> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.get<{ success: boolean; data: OnboardingProgress }>(
        `${API_URL}/api/onboarding/progress`,
        config
      );
      return response.data.data;
    } catch (error: any) {
      const status = error.response?.status;
      // Handle 404 (no progress yet), 401/403 (invalid/expired token) gracefully
      if (status === 404 || status === 401 || status === 403) {
        onboardingLogger.debug(`Onboarding progress check returned ${status}, returning null`);
        return null;
      }
      // Handle network errors gracefully (backend unreachable, timeout, etc.)
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error') || !error.response) {
        onboardingLogger.debug('Onboarding progress check: Network unavailable, returning null');
        return null;
      }
      onboardingLogger.error('Error getting progress:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to get progress'
      );
    }
  }

  /**
   * Get the legacy onboarding progress AND the new-wizard state in one
   * call. Used by SplashScreen to decide whether a signed-in user should
   * be routed into the new wizard, the legacy AI onboarding, or the
   * dashboard.
   */
  async getProgressAndWizardState(): Promise<{
    progress: OnboardingProgress | null;
    onboardingVersion: number;
    onboardingStep: string | null;
  }> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.get<{
        success: boolean;
        data: OnboardingProgress | null;
        onboardingVersion?: number;
        onboardingStep?: string | null;
      }>(`${API_URL}/api/onboarding/progress`, config);

      return {
        progress: response.data.data,
        onboardingVersion: response.data.onboardingVersion ?? 0,
        onboardingStep: response.data.onboardingStep ?? null,
      };
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 404 || status === 401 || status === 403) {
        onboardingLogger.debug(
          `Progress + wizard state check returned ${status}, returning defaults`,
        );
        return { progress: null, onboardingVersion: 0, onboardingStep: null };
      }
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error') || !error.response) {
        onboardingLogger.debug('Progress + wizard state check: network unavailable');
        return { progress: null, onboardingVersion: 0, onboardingStep: null };
      }
      onboardingLogger.error('Error getting progress + wizard state:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to get onboarding state',
      );
    }
  }

  /**
   * Phase 5 migration banner: should this v0 user see the "Try our
   * quicker setup" prompt on Dashboard?
   */
  async getMigrationStatus(): Promise<{ shouldOffer: boolean }> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.get<{ success: boolean; data: { shouldOffer: boolean } }>(
        `${API_URL}/api/onboarding/migration-status`,
        config,
      );
      return response.data.data;
    } catch (error) {
      // Fail safe — don't nag if we can't tell
      onboardingLogger.debug('Migration status check failed, assuming no offer', error);
      return { shouldOffer: false };
    }
  }

  /**
   * User tapped "No thanks" on the migration banner. Bumps them to v1
   * on the backend so the banner never reappears.
   */
  async dismissMigrationPrompt(): Promise<void> {
    const config = await this.getAuthHeader();
    await axios.post(`${API_URL}/api/onboarding/migration-dismiss`, {}, config);
  }

  /**
   * Letter-first flow (onboardingVersion 2): persist the user's step
   * pointer — 'letter' (the hook), 'levels' (declined, browsing tiers),
   * or 'complete' — so Splash/login resume in the right place. Fire and
   * mostly-forget: routing already happened locally, so a failure here
   * only costs resume accuracy, never blocks the user.
   */
  async setLetterStep(step: 'letter' | 'levels' | 'complete'): Promise<void> {
    try {
      const config = await this.getAuthHeader();
      await axios.post(`${API_URL}/api/onboarding/letter-step`, { step }, config);
    } catch (error) {
      onboardingLogger.debug(`Letter-step update to '${step}' failed (non-blocking)`, error);
    }
  }

  /**
   * Does this user need to finish setting up their successor?
   *
   * Dashboard calls this on load to decide whether to show the
   * "Choose your successor" banner. Returns hasSuccessor=true once
   * the user has a successor row, which the banner uses as its
   * dismissal signal.
   */
  async getSuccessorStatus(): Promise<{
    hasSuccessor: boolean;
    // Three-state successor signal so the dashboard banner can render
    // distinct "active" / "pending invitation" / "no successor" copy
    // and route to the right destination. Older backends won't return
    // this field, so consumers must tolerate `undefined` and fall back
    // to the `hasSuccessor` boolean.
    successorStatus?: 'active' | 'pending' | 'none';
    seed: { firstName?: string; email?: string } | null;
    // Set when successorStatus === 'pending' so the banner can show
    // "Invitation to <Name> is pending" copy.
    pendingInvitee?: { firstName?: string; email?: string } | null;
  }> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.get<{
        success: boolean;
        data: {
          hasSuccessor: boolean;
          successorStatus?: 'active' | 'pending' | 'none';
          seed: { firstName?: string; email?: string } | null;
          pendingInvitee?: { firstName?: string; email?: string } | null;
        };
      }>(`${API_URL}/api/onboarding/successor-status`, config);
      return response.data.data;
    } catch (error: any) {
      onboardingLogger.debug('Successor status check failed, assuming has successor to avoid false banner', error?.message);
      // Fail safe: don't nag if we can't tell — err on the side of not showing the banner
      return { hasSuccessor: true, seed: null };
    }
  }

  /**
   * Pause onboarding
   */
  async pauseOnboarding(): Promise<void> {
    try {
      const config = await this.getAuthHeader();
      await axios.post(
        `${API_URL}/api/onboarding/pause`,
        {},
        config
      );
    } catch (error: any) {
      onboardingLogger.error('Error pausing onboarding:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to pause onboarding'
      );
    }
  }

  /**
   * Skip onboarding
   */
  async skipOnboarding(): Promise<void> {
    try {
      const config = await this.getAuthHeader();
      await axios.post(
        `${API_URL}/api/onboarding/skip`,
        {},
        config
      );
    } catch (error: any) {
      onboardingLogger.error('Error skipping onboarding:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to skip onboarding'
      );
    }
  }

  /**
   * Complete onboarding
   */
  async completeOnboarding(): Promise<void> {
    try {
      const config = await this.getAuthHeader();
      await axios.post(
        `${API_URL}/api/onboarding/complete`,
        {},
        config
      );
      // Store locally that onboarding is complete
      await AsyncStorage.setItem('hasCompletedOnboarding', 'true');
    } catch (error: any) {
      onboardingLogger.error('Error completing onboarding:', error);
      // Still mark as complete locally even if API fails
      await AsyncStorage.setItem('hasCompletedOnboarding', 'true');
      throw new Error(
        error.response?.data?.message || 'Failed to complete onboarding'
      );
    }
  }

  /**
   * Get prioritized tasks
   */
  async getPriorities(): Promise<TaskPriority[]> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.get<PrioritiesResponse>(
        `${API_URL}/api/onboarding/priorities`,
        config
      );
      return response.data.data;
    } catch (error: any) {
      onboardingLogger.error('Error getting priorities:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to get priorities'
      );
    }
  }

  /**
   * Recalculate priorities based on responses
   */
  async recalculatePriorities(): Promise<TaskPriority[]> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.post<PrioritiesResponse>(
        `${API_URL}/api/onboarding/recalculate-priorities`,
        {},
        config
      );
      return response.data.data;
    } catch (error: any) {
      onboardingLogger.error('Error recalculating priorities:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to recalculate priorities'
      );
    }
  }

  /**
   * Send a message in the onboarding chat
   */
  async sendMessage(messages: Array<{ role: string; content: string }>): Promise<{
    message: string;
    progress: { current: number; total: number };
  }> {
    try {
      const config = await this.getAuthHeader();
      const response = await axios.post(
        `${API_URL}/api/onboarding/chat`,
        { messages },
        config
      );
      return response.data;
    } catch (error: any) {
      onboardingLogger.error('Error sending onboarding message:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to send message'
      );
    }
  }
}

export const onboardingService = new OnboardingService();
